import { useEffect, useRef, useState } from 'react';
import { appRoles } from '../../authConfig';
import PagedList, { IPagedListRefType } from '../../common/PagedList';
import {
  deleteLabel,
  exportLabels,
  getLabels,
  getLabelsets,
  ILabel,
  importLabels,
  type IImportLabelsResponse,
} from '../../services/labelService';
import { useAtomValue, useSetAtom } from 'jotai';
import { profileDataAtom } from '../../atoms/authAtoms';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Field,
  FluentProvider,
  Option,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import { Panel, PanelType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import ConfirmDialog from '../../common/ConfirmDialog';
import ImportPanel from '../../common/ImportPanel';
import { downloadCsvWithTimestamp } from '../../utils/csvHelpers';

const Labels = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const [selectedLabel, setSelectedLabel] = useState<ILabel>();
  const pagedListRef = useRef<IPagedListRefType>(null);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const [labelsets, setLabelsets] = useState<string[]>([]);
  const [labelsetName, setLabelsetName] = useState<string>();

  const isAdmin = profileData.roles.includes(appRoles.Admin);

  const [
    isDeleteConfirmOpen,
    { setTrue: showDeleteConfirm, setFalse: hideDeleteConfirm },
  ] = useBoolean(false);

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [selectedExportLabelSets, setSelectedExportLabelSets] = useState<
    string[]
  >([]);

  const openExportPanel = () => {
    setSelectedExportLabelSets(labelsetName ? [labelsetName] : []);
    setExportPanelOpen(true);
  };

  const handleExportLabels = async () => {
    if (selectedExportLabelSets.length === 0) return;

    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const csvContent = await exportLabels(
        abortController,
        selectedExportLabelSets
      );
      downloadCsvWithTimestamp('labels', csvContent);
      setSuccessMessage('Export completed. CSV downloaded.');
      setExportPanelOpen(false);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        const data: string[] = await getLabelsets(abortController);
        setLabelsets(data);
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
  }, [setErrorMessage, setIsInProgress]);

  return (
    <>
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: 420,
            marginBottom: 8,
          }}
        >
          <Text style={{ minWidth: 96 }}>Label Set</Text>
          <Dropdown
            style={{ width: 300 }}
            value={labelsetName ?? ''}
            selectedOptions={labelsetName ? [labelsetName] : []}
            onOptionSelect={(_event, data) => {
              const selected = data.optionValue?.toString();
              setLabelsetName(selected);
              setSelectedLabel(undefined);
              pagedListRef.current?.refresh();
            }}
          >
            {labelsets.map((ls) => (
              <Option key={ls} value={ls}>
                {ls}
              </Option>
            ))}
          </Dropdown>
        </div>
      </FluentProvider>
      <PagedList
        selectedTab="Labels"
        ref={pagedListRef}
        searchInToolbar={true}
        searchInputWidth={220}
        columns={[
          { key: 'id', minWidth: 45, maxWidth: 50 },
          { key: 'key', minWidth: 100, maxWidth: 150 },
          { key: 'label', minWidth: 200, maxWidth: 300 },
        ]}
        getAction={(
          abortController: AbortController,
          pageSize: number,
          pageNo: number,
          orderBy?: string | undefined,
          search?: string | undefined
        ) => {
          return getLabels(
            abortController,
            pageSize,
            pageNo,
            labelsetName ?? '',
            orderBy,
            search
          );
        }}
        commandBarItems={[
          {
            key: 'newLabel',
            text: 'New',
            iconProps: { iconName: 'Add' },
            onClick: () => {
              void navigate(
                `/newLabel?labelSet=${encodeURIComponent(labelsetName ?? '')}`
              );
            },
            disabled: !isAdmin,
          },
          {
            key: 'editLabel',
            text: 'Edit',
            iconProps: { iconName: 'Edit' },
            onClick: () => {
              if (selectedLabel) {
                void navigate(
                  `/editLabel?id=${
                    selectedLabel.id
                  }&labelSet=${encodeURIComponent(
                    labelsetName ?? ''
                  )}&key=${encodeURIComponent(
                    selectedLabel.key
                  )}&label=${encodeURIComponent(selectedLabel.label)}`
                );
              }
            },
            disabled: !isAdmin || !selectedLabel,
          },
          {
            key: 'deleteLabel',
            text: 'Delete',
            iconProps: { iconName: 'Delete' },
            onClick: () => showDeleteConfirm(),
            disabled: !isAdmin || !selectedLabel,
          },
          {
            key: 'importLabels',
            text: 'Import',
            iconProps: { iconName: 'Upload' },
            onClick: () => setImportPanelOpen(true),
            disabled: !isAdmin,
          },
          {
            key: 'exportLabels',
            text: 'Export',
            iconProps: { iconName: 'CloudDownload' },
            onClick: () => openExportPanel(),
            disabled: !isAdmin,
          },
        ]}
        onSelectionChange={(selection: any) => {
          setSelectedLabel(selection as ILabel);
        }}
      />
      <ImportPanel<IImportLabelsResponse>
        isOpen={importPanelOpen}
        headerText="Import Labels"
        onDismiss={() => setImportPanelOpen(false)}
        importApi={importLabels}
        getErrorsCount={(r) => r.errors.length}
        onImportSucceeded={(r) =>
          `Import completed. Created: ${r.createdCount}, Updated: ${r.updatedCount}.`
        }
        onAfterImport={() => pagedListRef.current?.refresh()}
        renderResults={(r) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <Text size={200}>Created: {r.createdCount}</Text>
            <Text size={200}>Updated: {r.updatedCount}</Text>
            {r.errors.length > 0 ? (
              <>
                <Text size={200}>Errors:</Text>
                {r.errors.map((error, index) => (
                  <Text key={`${error}-${index}`} size={200}>
                    {error}
                  </Text>
                ))}
              </>
            ) : null}
          </div>
        )}
      />
      <Panel
        isOpen={exportPanelOpen}
        onDismiss={() => setExportPanelOpen(false)}
        type={PanelType.medium}
        headerText="Export Labels"
      >
        <FluentProvider theme={webLightTheme}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              marginTop: 16,
            }}
          >
            <Field label="Label Sets">
              <Dropdown
                multiselect={true}
                selectedOptions={selectedExportLabelSets}
                onOptionSelect={(_e, data) => {
                  setSelectedExportLabelSets(data.selectedOptions);
                }}
              >
                {labelsets.map((ls) => (
                  <Option key={ls} value={ls}>
                    {ls}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <Button
                appearance="primary"
                onClick={() => {
                  void handleExportLabels();
                }}
                disabled={selectedExportLabelSets.length === 0}
              >
                Export
              </Button>
              <Button
                appearance="secondary"
                onClick={() => setExportPanelOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </FluentProvider>
      </Panel>
      <ConfirmDialog
        isModalOpen={isDeleteConfirmOpen}
        hideModal={hideDeleteConfirm}
        message={`The Label ${selectedLabel?.id} would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteConfirm();
          if (!selectedLabel) return;
          const abortController = new AbortController();
          deleteLabel(abortController, selectedLabel.id)
            .then(() => {
              pagedListRef.current?.refresh();
              setSuccessMessage(`Label with id: ${selectedLabel.id} deleted.`);
              setSelectedLabel(undefined);
            })
            .catch((error: any) => {
              setErrorMessage(error.message);
            });
        }}
      />
    </>
  );
};

export default Labels;
